import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import { DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import { useDocumentPreviewStyles } from "./DocumentPreview.styles";
import DocumentPreviewToolbar from "./DocumentPreviewToolbar";
import { useDocumentKeyboardNavigation } from "./DocumentPreview.hooks";
import { useCtrlWheelZoom } from "./useCtrlWheelZoom";
import { IDocumentNavigation, IDocumentPreviewToolbarAction } from "./DocumentPreview.types";
import DocumentPreviewFailed from "./DocumentPreviewFailed";

export interface IImageViewerProps {
    imageUrl?: string | null;
    fileName: string;
    zoomLevel: number;
    zoomInputValue: string;
    onZoomBy?: (factor: number) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onZoomInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onZoomInputBlur: () => void;
    onDownload: () => void;
    isDownloading: boolean;
    isLoading?: boolean;
    documentNavigation?: IDocumentNavigation;
    toolbarAction?: IDocumentPreviewToolbarAction;
}

export default function ImageViewer(props: IImageViewerProps) {
    const styles = useDocumentPreviewStyles();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    useCtrlWheelZoom(scrollContainerRef, props.onZoomBy);
    const [hasError, setHasError] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const dragStartRef = useRef({ x: 0, y: 0 });
    const positionStartRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (props.zoomLevel <= 1) {
            setPosition({ x: 0, y: 0 });
        }
    }, [props.zoomLevel]);

    useEffect(() => {
        setHasError(false);
        setPosition({ x: 0, y: 0 });
    }, [props.imageUrl]);

    useDocumentKeyboardNavigation(props.documentNavigation);

    const handleImageError = useCallback(() => {
        setHasError(true);
    }, []);

    const handleMouseDown = useCallback(
        (event: React.MouseEvent) => {
            if (props.zoomLevel <= 1) return;
            event.preventDefault();
            setIsDragging(true);
            dragStartRef.current = { x: event.clientX, y: event.clientY };
            positionStartRef.current = { x: position.x, y: position.y };
        },
        [props.zoomLevel, position]
    );

    const handleMouseMove = useCallback(
        (event: React.MouseEvent) => {
            if (!isDragging) return;
            const deltaX = event.clientX - dragStartRef.current.x;
            const deltaY = event.clientY - dragStartRef.current.y;
            setPosition({
                x: positionStartRef.current.x + deltaX,
                y: positionStartRef.current.y + deltaY,
            });
        },
        [isDragging]
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    const imageStyle = useMemo(
        () => ({
            ...styles.previewImage,
            transform: `scale(${props.zoomLevel}) translate(${position.x / props.zoomLevel}px, ${position.y / props.zoomLevel}px)`,
            cursor: props.zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default",
            userSelect: "none" as const,
        }),
        [styles.previewImage, props.zoomLevel, position.x, position.y, isDragging]
    );

    const renderContent = () => {
        if (props.isLoading) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <DotLegalFullScreenSpinner />
                </Box>
            );
        }

        if (hasError) {
            return <DocumentPreviewFailed />;
        }

        if (!props.imageUrl) {
            return null;
        }

        return (
            <Box sx={styles.viewerContainer} ref={scrollContainerRef}>
                <Box
                    sx={styles.imageContainer}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                >
                    <Box component="img" key={props.imageUrl} src={props.imageUrl} alt={props.fileName} onError={handleImageError} sx={imageStyle} />
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={styles.imageViewerContainer}>
            <DocumentPreviewToolbar
                zoomLevel={props.zoomLevel}
                zoomInputValue={props.zoomInputValue}
                onZoomIn={props.onZoomIn}
                onZoomOut={props.onZoomOut}
                onZoomInputChange={props.onZoomInputChange}
                onZoomInputKeyDown={props.onZoomInputKeyDown}
                onZoomInputBlur={props.onZoomInputBlur}
                onDownload={props.onDownload}
                isDownloading={props.isDownloading}
                documentNavigation={props.documentNavigation}
                toolbarAction={props.toolbarAction}
            />
            {renderContent()}
        </Box>
    );
}
